import { useCallback, useEffect, useState } from 'react';
import {
  actionType,
  initialAddressState,
  initialEmployeeState,
  serviceLevels,
  serviceSlotType,
  toastMessages,
} from 'config/constants';
import { ActivitiesTable } from './components';
import { Box, Grid, Typography } from '@material-ui/core';
import { checkFileTotalSize, createActivityBody } from 'utils';
import { isNil } from 'lodash';
import { ServiceApi } from 'services';
import { useAddresses, useAddressesFunctions } from 'contexts/AddressContext';
import { useCompany } from 'contexts/CompanyContext';
import { useEmployee, useEmployeeFunctions } from 'contexts/EmployeeContext';
import { useHistory } from 'react-router-dom';
import { useProvider, useProviderFunctions } from 'contexts/ProviderContext';
import { useTheme } from '@material-ui/core/styles';
import ActionOnActivityModal from 'components/ActionOnActivityModal';
import ActivityForm from 'components/ActivityForm';
import addActivityIcon from '_assets/activity/addActivity.svg';
import addActivityHoveredIcon from '_assets/activity/addActivityHovered.svg';
import addEmployeeIcon from '_assets/activity/addEmployee.svg';
import addEmployeeHoveredIcon from '_assets/activity/addEmployeeHovered.svg';
import addLocationIcon from '_assets/activity/addLocation.svg';
import addLocationHoveredIcon from '_assets/activity/addLocationHovered.svg';
import AddressForm from 'components/AddressForm';
import BlockWithoutStepModal, { ENDING_LOCATIONS } from 'components/BlockWithoutStepModal';
import CheckIcon from '@material-ui/icons/Check';
import DefaultIconButton from 'components/DefaultIconButton';
import DefaultModal from 'components/DefaultModal';
import DefaultSwitch from 'components/DefaultSwitch';
import EmployeeForm from 'components/EmployeeForm';
import routes from 'config/routes';
import SwitchStatus from 'components/SwitchStatus';
import toast from 'react-hot-toast';
import useShowActionModal from 'hooks/useShowActionModal';
import useStyles from './styles';

const getInitialActivityState = (activity) => {
  return {
    id: activity?.id ?? null,
    courseType: activity?.courseType?.value ?? serviceSlotType[0].value,
    description: activity?.description ?? '',
    duration: activity?.duration ?? '60',
    employeesAndAddresses: activity?.employeesAndAddresses ?? [],
    identifier: activity?.identifier ?? '',
    level: activity?.level?.value ?? 'beginner',
    maxAge: activity?.maxAge ?? '0',
    minAge: activity?.minAge ?? '0',
    priceHighSeason: activity?.priceHighSeason ?? '0',
    priceLowSeason: activity?.priceLowSeason ?? '0',
    productName: activity?.productName ?? '',
    publish: false,
    slots: activity?.slots ?? '1',
    status: activity?.status ?? 'enabled',
    ImgPath: activity?.ImgPath ?? null,
  };
};

const Activities = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();

  const { createEmployee } = useEmployeeFunctions();
  const { createAddress } = useAddressesFunctions();

  const { addresses } = useAddresses();
  const { currentCompany, companySteps, companyStepLoading } = useCompany();
  const { employees } = useEmployee();
  const { provider } = useProvider();
  const { editProvider } = useProviderFunctions();

  const [seasonPrice, setSeasonPrice] = useState(provider ? provider?.season_price === 'low_season' : false);

  const [activities, setActivities] = useState([]);
  const [activity, setActivity] = useState(getInitialActivityState(null));
  const [isNewActivity, setIsNewActivity] = useState(false);
  const [createModalsOpen, setCreateModalsOpen] = useState({ address: false, employee: false });
  // DO NOT REMOVE
  //const [initialEmployee, setInitialEmployee] = useState(null);

  const [actionLoading, setActionLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isPageBlocked, setPageBlocked] = useState(false);

  // const [isConflictingEventsViewVisible, setConflictingEventsViewIsVisible] = useState(false);
  // const [conflictingEventsHours, setConflictingEventsHours] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const openActivityForm = Boolean(anchorEl);

  const { currentEntity, resetShowAction, setActionConflictSelect, shouldShowAction, showAction } = useShowActionModal(
    actionType.DELETE,
    'activity',
    deleteActivity,
  );

  useEffect(() => {
    setPageBlocked(
      !companySteps.agenda_created ||
        !companySteps.description_created ||
        !companySteps.employee_created ||
        !companySteps.place_created ||
        !companySteps.service_created,
    );
  }, [companySteps]);

  const getActivities = useCallback(() => {
    setIsTableLoading(true);
    ServiceApi.listCompanyServices(currentCompany.id)
      .then(({ data }) => {
        const activities = data.map((service) => ({
          id: service.id,
          productName: service.name,
          productID: service.productID,
          ImgPath: service.imageURL,
          description: service.description,
          level: serviceLevels.find((l) => l.value === service.service_level) ?? serviceLevels[3],
          courseType: serviceSlotType.find((st) => st.value === service.slot_type) ?? serviceSlotType[0],
          courseTypeColor: service.slot_type === 'public' ? 'default' : 'primary',
          slots: service.clients_number,
          duration: service.duration,
          minAge: service.min_age,
          maxAge: service.max_age,
          priceHighSeason: service.peak_season_price,
          priceLowSeason: service.low_season_price,
          status: service.status,
          identifier: service.identifier,
          employeesAndAddresses: service.employees.map((emp) => ({
            address: addresses.find((a) => a.id === emp.services_addresses_employees_associations.address_id),
            employee: employees.find((e) => e.id === emp.id),
          })),
        }));

        setActivities(activities);
        setIsTableLoading(false);
      })
      .catch(() => {
        toast.error(toastMessages.error.FETCH_ACTIVITIES);
        setIsTableLoading(false);
      });
  }, [addresses, currentCompany.id, employees]);

  useEffect(() => {
    getActivities();
  }, [getActivities]);

  const handleCreateActivity = async (values, picture) => {
    let formData = createActivityBody(values);
    if (picture.file) {
      if (!checkFileTotalSize([picture.file])) {
        toast.error(toastMessages.error.UPLOAD_PICTURE_SIZE_LIMIT);
        return { pictureSizeError: true };
      }

      formData.append('serviceImage', picture.file);
    }

    try {
      await ServiceApi.createCompanyService(currentCompany.id, formData);
      toast.success(toastMessages.success.CREATE_ACTIVITY);
      return { success: true };
    } catch (error) {
      toast.error(toastMessages.error.CREATE_ACTIVITY);
      return { success: false };
    }
  };

  async function editActivity(formData) {
    try {
      const response = await ServiceApi.updateCompanyService(activity.id, formData);
      if (response.status === 'CONFLICT') {
        return { conflict: true, success: false };
      }
      toast.success(toastMessages.success.UPDATE_ACTIVITY);
      getActivities();
      return { success: true };
    } catch (e) {
      toast.error(toastMessages.error.UPDATE_ACTIVITY);
      return { success: false };
    }
  }

  const handleEditActivity = async (values, picture) => {
    let formData = createActivityBody(values);
    if (picture.file) {
      if (!checkFileTotalSize([picture.file])) {
        toast.error(toastMessages.error.UPLOAD_PICTURE_SIZE_LIMIT);
        return { pictureSizeError: true };
      }

      formData.append('Image', picture.file);
    }

    return await editActivity(formData);
  };

  const handleStatusChanged = async ({ enabled, force }) => {
    let formData = new FormData();
    formData.append('force', force);
    formData.append('status', enabled ? 'enabled' : 'disabled');

    return await editActivity(formData);
  };

  async function deleteActivity(force = false) {
    try {
      const response = await ServiceApi.deleteCompanyService(currentEntity, { force });

      // Manage conflict response
      if (response.status === 'CONFLICT') {
        return { conflict: true, success: false };
      }

      // Manage NO DELETE response
      if (response.status === 'NO_DELETE') {
        getActivities();
        return { success: false };
      }

      getActivities();
      toast.success(toastMessages.success.DELETE_ACTIVITY);
      return { success: true };
    } catch (error) {
      toast.error(toastMessages.error.DELETE_ACTIVITY);
      return { success: false };
    }
  }

  const handleSeasonPriceChange = async ({ target: { checked } }) => {
    setSeasonPrice(checked);
    editProvider({ season_price: checked ? 'low_season' : 'peak_season' }).then((changed) => {
      if (!changed) setSeasonPrice(!checked);
    });
  };

  const handleBlockedPageClose = (event) => {
    event.preventDefault();

    history.push(routes.home.root);
  };

  const handleCreateAddress = async (values) => {
    const body = {
      address: values.address_name,
      city: values.city_name,
      title: values.title,
      index: values.street_index,
      number: values.street_number,
      zipCode: values.zip_code,
    };

    const { created } = await createAddress(body);
    if (created) setCreateModalsOpen((prev) => ({ ...prev, address: false }));

    return { success: created };
  };

  const handleCreateEmployee = async ({ values, picture }) => {
    let pictureData = null;
    if (picture?.file) {
      let formData = new FormData();
      formData.append('file', picture?.file);
      pictureData = { data: formData, url: picture?.url };
    }

    const { created, pictureUpdated } = await createEmployee(values, pictureData);
    if (created) setCreateModalsOpen((prev) => ({ ...prev, employee: false }));

    return { pictureUpdated, success: created };
  };

  const onCreateEmployeeClick = () => {
    setCreateModalsOpen((prev) => ({ ...prev, employee: true }));
  };

  const onCreateAddressClick = () => {
    setCreateModalsOpen((prev) => ({ ...prev, address: true }));
  };
  // DO NOT REMOVE
  /*const editActivityEmployees = async (monitor) => {
    try {
      const { conflictingEvents, status } = await ServiceApi.updateServiceEmployees(activity.id, { employee_id: monitor.id });
      if (status === 'KO' && conflictingEvents && conflictingEvents.length > 0) {
        setConflictingEventsHours(conflictingEvents.map((event) => {
          return {
            id: event.target.id,
            dayNumber: event.day_number,
            serviceName: event.current.service.name,
            serviceDuration: event.current.service.duration,
            serviceId: event.current.service.id,
            startHour: event.current.startHour,
            startMinute: event.current.startMinute,
            endHour: event.current.endHour,
            endMinute: event.current.endMinute,
            employeeId: event.currentEmployee.id,
            targetEmployeeId: event.targetEmployee.id
          };
        }));
        setConflictingEventsViewIsVisible(true);
      }
      else setConflictingEventsViewIsVisible(false);
    } catch (e) {
      toast.error(toastMessages.error.UPDATE_EMPLOYEE_ACTIVITY);
    }
  }*/

  const onAddNewActivityClick = (event) => {
    setActivity(getInitialActivityState(null));
    setAnchorEl(event.currentTarget);
    setIsNewActivity(true);
  };

  const onDeleteActivityClick = async (event, index) => {
    shouldShowAction({ entity: activities[index].id });
  };

  const onEditActivityClick = (event, selectedActivity) => {
    setActivity(getInitialActivityState(selectedActivity));
    //setInitialEmployee(selectedActivity.employee);
    setAnchorEl(event.currentTarget);
  };

  const handleDeleteActivity = async (force = false) => {
    setActionLoading(true);
    const response = await deleteActivity(force);
    setActionLoading(false);
    return response;
  };

  const handleConfirmActionOnAddressModal = async ({ forceAction }) => {
    // Handle confirmation on delete
    if (!isNil(forceAction)) {
      if (forceAction) await handleDeleteActivity(true);
      return resetShowAction();
    }

    // Handle preference passed
    const { conflict } = await handleDeleteActivity();
    if (conflict) return setActionConflictSelect();
    resetShowAction();
  };

  const handleCloseForm = () => {
    setAnchorEl(null);
    setIsNewActivity(false);
  };

  const handleSubmitForm = async (values, picture) => {
    const result = isNewActivity
      ? await handleCreateActivity(values, picture)
      : await handleEditActivity(values, picture);

    if (result.success) {
      // TODO: adapt to all employees from the activity
      /*if (!isNewActivity && (!initialEmployee || initialEmployee?.id !== values.employee.id)) {
        await editActivityEmployees(values.employee);
      }*/

      getActivities();
      handleCloseForm();

      if (isNewActivity && values.publish) {
        history.push('/social-networks', {
          post: {
            comment: values.description,
            file: {
              data: picture.file,
              type: 'image',
              url: picture.url,
            },
          },
        });
      }
    }
  };

  /*const handleCancelConflictingHoursChanges = () => {
    setConflictingEventsViewIsVisible(false);
  };

  const handleSubmitConflictingHoursChanges = () => {
    AgendaApi.updateConflictingEventsHours({ conflicting_opening_hours: conflictingEventsHours })
      .then(
        ({ conflictingEvents, status }) => {
          if (conflictingEvents && conflictingEvents.length > 0) setConflictingEventsHours(conflictingEvents);
          else if (status === 'OK') {
            setConflictingEventsViewIsVisible(false);
            toast.success(toastMessages.success.RESOLVED_CONFLICTS);
          }
        })
      .catch(() => {
        toast.error(toastMessages.error.RESOLVED_CONFLICTS);
      });
  };*/

  return (
    <Grid container justifyContent="center" alignItems="center" spacing={3}>
      <Grid container item xs={12} justifyContent="center">
        <Typography variant="h1">Activités</Typography>
      </Grid>

      <Grid container item xs={12} spacing={3}>
        <Grid item xs={12}>
          <Box className={classes.actionsBar}>
            <Box className={classes.seasonPriceContainer}>
              <Typography>Activer les prix basse saison</Typography>
              <DefaultSwitch
                checked={seasonPrice}
                checkedIcon={CheckIcon}
                color={theme.palette.primary.main}
                onChange={handleSeasonPriceChange}
              />
              <SwitchStatus status={seasonPrice} />
            </Box>
            <Box style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
              <DefaultIconButton
                color="primary"
                variant="contained"
                onClick={onCreateEmployeeClick}
                icon={addEmployeeIcon}
                hoveredIcon={addEmployeeHoveredIcon}
                iconAlt="Icone Ajout d'employee"
                iconText="Moniteur"
              />
              <DefaultIconButton
                color="primary"
                variant="contained"
                onClick={onCreateAddressClick}
                icon={addLocationIcon}
                hoveredIcon={addLocationHoveredIcon}
                iconAlt="Icone Ajout de lieux"
                iconText="Lieux"
              />
              <DefaultIconButton
                color="primary"
                variant="contained"
                onClick={onAddNewActivityClick}
                icon={addActivityIcon}
                hoveredIcon={addActivityHoveredIcon}
                iconAlt="Icone Ajout d'activité"
                iconText="activité"
              />
            </Box>
          </Box>

          <ActivitiesTable
            data={activities}
            loading={isTableLoading && companyStepLoading}
            onEditClick={onEditActivityClick}
            onRemoveClick={onDeleteActivityClick}
            seasonPrice={seasonPrice}
          />
        </Grid>
      </Grid>

      <DefaultModal
        className={classes.activityModal}
        hideActions={true}
        open={openActivityForm}
        title={`${isNewActivity ? 'Ajouter' : 'Modifier'} une activité`}
        onClose={handleCloseForm}
      >
        <ActivityForm
          activity={activity}
          dialog
          onCancel={handleCloseForm}
          onStatusChange={handleStatusChanged}
          onSubmit={handleSubmitForm}
          socialToggle={isNewActivity}
        />
      </DefaultModal>

      <DefaultModal hideActions maxWidth="md" open={createModalsOpen.employee} title="Création d'un moniteur">
        <EmployeeForm
          id="employeeFormActivity"
          dialog
          employee={initialEmployeeState}
          onCancel={() => setCreateModalsOpen((prev) => ({ ...prev, employee: false }))}
          onSubmit={handleCreateEmployee}
        />
      </DefaultModal>

      <DefaultModal hideActions maxWidth="md" open={createModalsOpen.address} title="Création d'un lieu">
        <AddressForm
          id="addressFormActivity"
          address={initialAddressState}
          dialog
          onCancel={() => setCreateModalsOpen((prev) => ({ ...prev, address: false }))}
          onSubmit={handleCreateAddress}
        />
      </DefaultModal>

      <BlockWithoutStepModal
        endingLocation={ENDING_LOCATIONS.activity}
        open={isPageBlocked && !openActivityForm}
        onClose={handleBlockedPageClose}
      />

      <ActionOnActivityModal
        loading={actionLoading}
        modalType={actionType.DELETE}
        onClose={resetShowAction}
        onConfirm={handleConfirmActionOnAddressModal}
        showAction={showAction}
      />

      {/*<ConflictDialog
        conflictingHours={conflictingEventsHours}
        onCancel={handleCancelConflictingHoursChanges}
        onValidate={handleSubmitConflictingHoursChanges}
        open={isConflictingEventsViewVisible}
        setConflictingHours={setConflictingEventsHours}
      />*/}
    </Grid>
  );
};

export default Activities;
